import React, { useState, useEffect } from "react";
import { Switch } from 'antd';
import { connect, useDispatch } from 'react-redux';
import { getWorkspaceById, getProjectTemplates, addProject } from "../../actions";
import { resetDrawer } from "../../actions/toggleAction";
import { getSectionColor } from "../../js/helper";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from 'react-router-dom';


const AddProject = (props) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const {
    templates,
    workspaceId,
  } = props;

  const [selectedTemplate, setSelectedTemplate] = useState(templates?.find(t => t?.default));
  const [projectName, setProjectName] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);

  const isButtonDisabled = !selectedTemplate || !projectName;

  const navigate = useNavigate();

  useEffect(() => {
    dispatch(getProjectTemplates());
  }, []);

  useEffect(() => {
    setSelectedTemplate(templates?.find(t => t?.default))
  }, [templates]);

  const createProject = async (name, templateId, isPrivate) => {
    if (isButtonDisabled) return;

    const project = await dispatch(
      addProject(
        {
          title: name,
          workspace: workspaceId,
          template: templateId,
          access: isPrivate ? 'private' : 'public'
        },
        async () => {
          setSelectedTemplate({ title: 'Select Team' });
          setSelectedTemplate(undefined);
          setProjectName('');
          dispatch(getWorkspaceById());
          dispatch(resetDrawer());
        }
      )
    );

    if (project?._id) {
      navigate(`/${project._id}/board`);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex flex-col justify-end p-5 sm:p-8 flex-shrink-0 min-h-[100px] sm:min-h-[160px]" style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)' }}>
        <div className="text-white font-black text-xl sm:text-2xl">
          <FormattedMessage id="CREATE_PROJECT" defaultMessage="Create Project" />
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-5">
        {/* Project name */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            <FormattedMessage id="PROJECT_NAME" defaultMessage="Project Name" />
          </label>
          <input
            type="text"
            value={projectName}
            placeholder={intl.formatMessage({ id: 'NAME_YOUR_PROJECT', defaultMessage: 'Name your new project' })}
            onChange={e => setProjectName(e?.target?.value)}
            className="w-full bg-white text-sm font-medium text-slate-800 px-4 py-2.5 rounded-lg outline-none transition duration-150"
            style={{ border: '1.5px solid rgba(99,102,241,0.35)', boxShadow: 'none' }}
            onFocus={e => { e.target.style.border = '1.5px solid #4f46e5'; e.target.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.12)'; }}
            onBlur={e => { e.target.style.border = '1.5px solid rgba(99,102,241,0.35)'; e.target.style.boxShadow = 'none'; }}
          />
        </div>

        {/* Template */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            <FormattedMessage id="TEMPLATE" defaultMessage="Template" />
          </label>
          <div className="flex flex-wrap gap-2">
            {templates.map(template => {
              const isSelected = selectedTemplate?._id === template._id;
              return (
                <button
                  key={template._id}
                  onClick={() => setSelectedTemplate(template)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 cursor-pointer"
                  style={isSelected ? {
                    background: 'rgba(99,102,241,0.1)',
                    border: '1.5px solid #4f46e5',
                    color: '#4f46e5',
                  } : {
                    background: '#fff',
                    border: '1.5px solid rgba(99,102,241,0.2)',
                    color: 'rgba(15,15,25,0.65)',
                  }}
                >
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ background: isSelected ? '#4f46e5' : 'rgba(99,102,241,0.3)' }}
                  />
                  {intl.formatMessage({ id: `${template?.name}`.replaceAll(' ', '_').toUpperCase(), defaultMessage: template?.name })}
                </button>
              );
            })}
          </div>
          {selectedTemplate && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {selectedTemplate.sections?.map((section, i) => {
                const color = getSectionColor(i);
                return (
                  <span
                    key={i}
                    className="text-xs px-2.5 py-1 rounded-full font-semibold"
                    style={{ background: color?.bg, color: 'rgba(15,15,25,0.7)', border: `1.5px solid ${color?.border}` }}
                  >
                    {intl.formatMessage({ id: `${section.title}`.replaceAll(' ', '_').toUpperCase(), defaultMessage: section.title })}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* Make private */}
        <div className="flex items-start justify-between gap-4 py-2">
          <div className="flex-1 min-w-0">
            <div className="text-base font-bold">
              <FormattedMessage id="MAKE_PRIVATE" defaultMessage="Make private" />?
            </div>
            <div className="text-sm mt-1 text-gray-500">
              <FormattedMessage
                id="MAKE_PRIVATE_DESCRIPTION"
                defaultMessage="Private projects shield sensitive data from individuals who are not part of the project"
              />
            </div>
          </div>
          <Switch checked={isPrivate} onChange={() => setIsPrivate(!isPrivate)} className="flex-shrink-0 mt-0.5" style={isPrivate ? { background: '#4f46e5' } : {}} />
        </div>
      </div>

      {/* Create button */}
      <div className="flex-shrink-0 p-4 border-t border-gray-100">
        <div
          onClick={() => { createProject(projectName, selectedTemplate?._id, isPrivate); setProjectName(''); }}
          className={`w-full text-center px-4 py-2.5 text-sm font-semibold text-white rounded-lg transition-colors ${isButtonDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
          style={{ background: isButtonDisabled ? 'rgba(99,102,241,0.5)' : '#4f46e5' }}
        >
          <FormattedMessage id="CREATE_PROJECT" defaultMessage="Create Project" />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  templates: state.projectReducer.publicTemplates,
});

export default connect(mapStateToProps)(AddProject);
